import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MouseEventHandler, useEffect } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import { updateOutlet } from '../../../apis/queries';
import SubmitButton from '../../../components/Buttons/SubmitButton';
import { isValidPhoneNumber } from '../../../helpers/validation';
import { Outlet } from '../../../types';
import DisplayFieldErrors from '../../../utils/DisplayFormError';
import { formatDateTime } from '../../../utils/formatDateTime';
import { notification, NotificationKinds } from '../../../widgets/Notifications/notifications';

interface UpdateOutletFormInput {
  name: string;
  phoneNumber: string;
  address: string;
  isActive: boolean;
}

const OutletDetailForm = ({
  outlet,
  isEditMode,
  onEditFormSubmit
}: {
  outlet: Outlet;
  isEditMode: boolean;
  isEditFormSubmitting: boolean;
  onEditFormSubmit: (value: boolean) => void;
}) => {
  const { name, phoneNumber, address, isActive, _id, createdAt, updatedAt } = outlet;
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: UpdateOutletFormInput) => {
      return updateOutlet(String(_id), {
        name: data.name,
        phoneNumber: data.phoneNumber,
        address: data.address,
        isActive: data.isActive
      });
    },
    onSuccess: data => {
      const { data: updatedOutlet } = data;
      notification({
        variant: NotificationKinds.SUCCESS,
        title: `OUTLET UPDATED`,
        subtitle: `${updatedOutlet?.name} has been updated`
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['outlets', { id: _id }] });
    }
  });

  const loading = mutation?.isPending;

  const createdBy = outlet.createdBy.fullName ?? '';

  useEffect(() => {
    onEditFormSubmit(loading);
  }, [loading]);

  const methods = useForm<UpdateOutletFormInput>({
    mode: 'onBlur',
    defaultValues: {
      name,
      phoneNumber,
      address,
      isActive
    }
  });

  const {
    register,
    formState: { isValid, errors, isDirty },
    reset,
    getValues
  } = methods;

  const onUpdateOutlet: MouseEventHandler<HTMLButtonElement> = event => {
    event.preventDefault();
    const fields = getValues();
    mutation?.mutate(fields);
  };

  const disabled = loading || !isEditMode;

  const handleCancel = () => {
    reset();
  };

  return (
    <Form>
      <Form.Group className="my-4">
        <Form.Check
          className="fw-bold"
          type="switch"
          label={'Activate Outlet'}
          {...register('isActive', { disabled })}
        />
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Outlet Name</Form.Label>
        <Form.Control
          type="text"
          placeholder="John Doe"
          {...register('name', {
            required: 'Outlet Name is required',
            validate: value => value.trim() !== '' || 'Product name cannot be empty',
            pattern: {
              value: /^[a-zA-Z0-9 ]+$/,
              message: 'Product name should contain only alphabets'
            },
            disabled,
            minLength: { value: 3, message: 'Name must be atleast 3 characters' }
          })}
        />
        {errors?.name && <DisplayFieldErrors displayText={errors.name.message!} />}
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Phone Number</Form.Label>
        <Form.Control
          type="text"
          placeholder="08039283847"
          {...register('phoneNumber', {
            required: 'Phone number is required',
            pattern: {
              value: /^[0-9]+$/,
              message: 'Phone number must contain only digits'
            },
            disabled,
            validate: isValidPhoneNumber
          })}
        />
        {errors?.phoneNumber && <DisplayFieldErrors displayText={errors.phoneNumber.message!} />}
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Address</Form.Label>
        <Form.Control
          type="text"
          placeholder="No 20 lekki phase 1"
          {...register('address', {
            required: 'Address is required',
            minLength: {
              value: 3,
              message: 'Address must be at least 5 characters long.'
            },
            validate: value => value.trim() !== '' || 'Address cannot be empty',
            disabled
          })}
        />
        {errors?.address && <DisplayFieldErrors displayText={errors.address.message!} />}
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Created By</Form.Label>
        <Form.Control type="text" placeholder="Created By" disabled={true} value={createdBy.toString()} />
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Created At</Form.Label>
        <Form.Control type="text" placeholder="Created At" disabled={true} value={formatDateTime(createdAt)} />
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Last Updated</Form.Label>
        <Form.Control type="text" placeholder="Updated At" disabled={true} value={formatDateTime(updatedAt)} />
      </Form.Group>

      {isEditMode && (
        <Row className="my-5">
          <Col>
            <SubmitButton
              type="button"
              buttonText="cancel"
              variant="white"
              onClick={handleCancel}
              disabled={disabled}
              loading={false}
            />
          </Col>
          <Col>
            <SubmitButton
              type="submit"
              buttonText="Submit"
              onClick={onUpdateOutlet}
              loading={loading}
              disabled={!isDirty || !isValid || disabled}
            />
          </Col>
        </Row>
      )}
    </Form>
  );
};

export default OutletDetailForm;
